import router from "@/router";
import RouteNames from "@/router/RouteNames.ts";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import ViewMode from "@/utils/constants/ViewMode.ts";
import SaveBehavior from "@/utils/constants/SaveBehavior.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import TopPanelSearchUtils, {SearchItem} from "@/utils/common/TopPanelSearchUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import MetaData from "@/utils/common/MetaData.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import {useLoginInfoStore} from "@/stores/loginInfo.ts";
import {useErrorsStore, useMessagesStore} from "@/stores/ToastDataStore.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import DocsCaptureMode from "@/utils/common/DocsCaptureMode.ts";
import {useThemeStore} from "@/stores/ThemeDataStore.ts";
import {useToast} from "vue-toastification";
import {getGridApi, type GridApi} from "ag-grid-community";
import {nextTick} from "vue";

const AUTOMATION_API_VERSION = 4;
const DEFAULT_TIMEOUT_MS = 60000;
const POLL_INTERVAL_MS = 10;

export interface GotoOptions {
  entityType: string;
  id?: string;
  code?: string;
  view?: string;
  pageId?: number;
  focusOnField?: string;
  editViewName?: string;
  timeoutMs?: number;
}

export interface AutomationState {
  loggedIn: boolean;
  routeName: string;
  entityType?: string;
  view?: string;
  id?: string;
  pageId?: number;
  ready: boolean;
  loading: boolean;
  dirty: boolean;
  errorCount: number;
  dialogOpen: boolean;
}

export default class NamaAutomation {
  static readonly version = AUTOMATION_API_VERSION;

  // Debug mode keeps the unrestricted setField that test and documentation setups rely on.
  private static isDebugMode(): boolean {
    return (window as any).namaDebugMode === true;
  }

  static state(): AutomationState {
    const query = this.currentQuery();
    const presenter = this.editPresenter();
    return {
      loggedIn: useLoginInfoStore().isLoggedIn,
      routeName: String(router.currentRoute.value.name ?? ""),
      entityType: query[PlaceTokens.PRM_ENTITY_TYPE],
      view: query[PlaceTokens.PRM_VIEW],
      id: query[PlaceTokens.PRM_ID],
      pageId: this.parsedPageId(query[PlaceTokens.PRM_PAGE_ID]),
      ready: this.ready(),
      loading: this.isLoading(),
      dirty: presenter ? presenter.isDirty() === true : false,
      errorCount: useErrorsStore().errors.length,
      dialogOpen: this.openDialogs().length > 0,
    };
  }

  static ready(): boolean {
    if (!useLoginInfoStore().isLoggedIn)
      return false;
    if (this.isLoading())
      return false;
    if (this.onListRoute())
      return (window as any).isScreenReady?.() === true && !this.listRowsLoading();
    if (!this.onEditRoute())
      return true;
    return this.editScreenLoaded();
  }

  // Deliberately independent of window.isScreenReady, which exists only while an edit screen is
  // mounted, and not at all before the 2026-08-23 EditView fix. These are the refs it itself reads.
  private static editScreenLoaded(): boolean {
    const presenter = this.editPresenter();
    return !!presenter && presenter.validLayout?.value === true && presenter.recordLoaded?.value === true;
  }

  static async whenReady(timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<AutomationState> {
    await this.waitForSettled(() => this.ready(), timeoutMs, "screen did not become ready");
    return this.state();
  }

  static async login(user: string, password: string, timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<AutomationState> {
    if (useLoginInfoStore().isLoggedIn)
      return this.whenReady(timeoutMs);
    // loginByUserAndPassword is registered by the SignIn component and nulled on unmount, so it
    // exists only while the login form is actually showing.
    await this.waitFor(() => useLoginInfoStore().isLoggedIn || typeof (window as any).loginByUserAndPassword === "function",
      timeoutMs, "login form never appeared");
    if (useLoginInfoStore().isLoggedIn)
      return this.whenReady(timeoutMs);
    useErrorsStore().clearErrors();
    (window as any).loginByUserAndPassword(user, password);
    await this.waitForSettled(() => useLoginInfoStore().isLoggedIn || !this.isLoading(), timeoutMs, "login did not complete");
    if (useLoginInfoStore().isLoggedIn)
      return this.whenReady(timeoutMs);
    // The OTP screen replaces the login form, which unregisters loginByUserAndPassword.
    if (typeof (window as any).loginByUserAndPassword !== "function")
      throw new Error("login needs a verification code: enter it in the browser, then call whenReady()");
    throw new Error("login failed: " + this.firstErrorMessage("no message from the server"));
  }

  static async goto(options: GotoOptions): Promise<AutomationState> {
    const place = this.placeFor(options);
    if (!place)
      throw new Error("goto requires an entityType");
    if (ObjectChecker.isNotEmptyOrNull(options.code))
      place.query[PlaceTokens.PRM_CODE] = options.code;
    if (ObjectChecker.isNotNullOrUndefined(options.pageId))
      place.pageId = options.pageId;
    if (ObjectChecker.isNotEmptyOrNull(options.focusOnField))
      place.focusOnField = options.focusOnField;
    if (ObjectChecker.isNotEmptyOrNull(options.editViewName))
      place.editViewName = options.editViewName;
    if (ObjectChecker.isNotEmptyOrNull(options.view))
      place.view = options.view;
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    useErrorsStore().clearErrors();
    await this.push({name: RouteNames.EDIT_VIEW_ROUTE, query: place.fetchQuery()}, timeoutMs);
    await this.waitForSettled(() => this.ready() || ObjectChecker.isNotEmptyOrNull(this.loadFailure()), timeoutMs, "screen did not become ready");
    const failure = this.loadFailure();
    if (ObjectChecker.isNotEmptyOrNull(failure))
      throw new Error("navigation failed: " + failure);
    this.verifyShownRecord(options);
    return this.state();
  }

  private static loadFailure(): string | undefined {
    const presenter = this.editPresenter();
    if (ObjectChecker.isNotEmptyOrNull(presenter?.recordLoadError?.value))
      return presenter.recordLoadError.value;
    if (presenter?.validLayout?.value === false || (!this.isLoading() && useErrorsStore().errors.length > 0 && !this.ready()))
      return this.firstErrorMessage("the screen did not load");
    return undefined;
  }

  private static verifyShownRecord(options: GotoOptions): void {
    const record = this.editPresenter()?.fetchRecord() ?? {};
    if (ObjectChecker.isNotEmptyOrNull(options.id) && ObjectChecker.areNotEqual(record.id, options.id))
      throw new Error("navigation failed: asked for id " + options.id + " but the screen shows " + (record.id ?? "no record"));
    if (ObjectChecker.isNotEmptyOrNull(options.code) && ObjectChecker.areNotEqual(record.code, options.code))
      throw new Error("navigation failed: asked for code " + options.code + " but the screen shows " + (record.code ?? "no record"));
  }

  static async gotoList(entityType: string, timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<AutomationState> {
    await this.push({name: RouteNames.LIST_VIEW_ROUTE, query: {[PlaceTokens.PRM_ENTITY_TYPE]: entityType}}, timeoutMs);
    return this.whenReady(timeoutMs);
  }

  static async searchMenu(query: string) {
    const matches = await TopPanelSearchUtils.searchFor(query);
    return (matches ?? []).map(item => this.serializableSearchItem(item));
  }

  static async openMenu(query: string, matchIndex: number = 0, timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<AutomationState> {
    const matches = await TopPanelSearchUtils.searchFor(query);
    const match = (matches ?? [])[matchIndex];
    if (!match)
      throw new Error("no menu match at index " + matchIndex + " for " + query);
    const route = TopPanelSearchUtils.resolveSearchItemRoute(match);
    if (!route)
      throw new Error("menu match does not resolve to a route: " + match.text);
    await this.push(route, timeoutMs);
    return this.whenReady(timeoutMs);
  }

  static fields(filter: string = "") {
    return TopPanelSearchUtils.searchForFields(filter).map(field => ({fieldId: field.id, label: field.text}));
  }

  static async focusField(fieldId: string, dontFlash?: boolean): Promise<boolean> {
    const matches = TopPanelSearchUtils.searchForFields(fieldId);
    if (!matches.length)
      return false;
    useNamaFlagsStore().setFieldToFocusOptions({field: matches[0], dontFlash});
    await this.whenReady();
    return true;
  }

  static getField(fieldId: string, row?: number): any {
    return this.toPlainValue(this.readPath(this.requireEditPresenter().fetchRecord(), fieldId, row));
  }

  static async setField(fieldId: string, value: any, row?: number) {
    const presenter = this.requireEditPresenter();
    if (!this.isDebugMode() && this.isLockedOnScreen(presenter, fieldId, row))
      throw new Error("field " + fieldId + " is disabled on this screen");
    this.writePath(presenter.fetchRecord(), fieldId, value, row);
    await presenter.fetchDispatcher().validate(fieldId, row);
    await this.whenReady();
    return {value: this.getField(fieldId, row), error: this.fieldError(fieldId), errors: this.errors()};
  }

  static fieldError(fieldId: string): string {
    return this.requireEditPresenter().fetchFieldErrorMsg(fieldId) ?? "";
  }

  static async save(behavior: string = SaveBehavior.SaveAndCommit, timeoutMs: number = DEFAULT_TIMEOUT_MS) {
    const presenter = this.requireEditPresenter();
    useErrorsStore().clearErrors();
    const outcome = await this.runUntilDialog(() => presenter.doSave(behavior, false, undefined, false), timeoutMs);
    return {saved: outcome.result === true, pending: outcome.pending, dialog: outcome.dialog, errors: this.errors(), state: this.state()};
  }

  static async runAction(actionId: string, timeoutMs: number = DEFAULT_TIMEOUT_MS) {
    const presenter = this.requireEditPresenter();
    useErrorsStore().clearErrors();
    const outcome = await this.runUntilDialog(() => presenter.callGUIAction(actionId), timeoutMs);
    return {pending: outcome.pending, dialog: outcome.dialog, errors: this.errors(), state: this.state()};
  }

  // A server confirmation or an action's question opens a dialog and the operation waits on it, so
  // awaiting the operation alone would stall until someone answers. Hand control back as soon as a
  // new dialog shows; the operation carries on once the dialog is answered.
  private static async runUntilDialog(operation: () => Promise<any>, timeoutMs: number) {
    const dialogsBefore = this.openDialogs();
    let finished = false;
    let result: any;
    let failure: any;
    operation().then(value => result = value, error => failure = error).finally(() => finished = true);
    let newDialog: HTMLElement | undefined;
    await this.waitFor(() => finished || !!(newDialog = this.openDialogs().find(dialog => !dialogsBefore.includes(dialog))),
      timeoutMs, "operation did not finish");
    if (newDialog && !finished)
      return {pending: true, dialog: this.dialogText(newDialog), result: undefined};
    if (failure)
      throw failure;
    await this.whenReady(timeoutMs);
    return {pending: false, dialog: "", result};
  }

  private static openDialogs(): HTMLElement[] {
    return Array.from(document.querySelectorAll<HTMLElement>(".q-dialog"));
  }

  private static dialogText(dialog: HTMLElement): string {
    return (dialog.querySelector<HTMLElement>(".q-card") ?? dialog).innerText.trim();
  }

  private static firstErrorMessage(fallback: string): string {
    return this.errors()[0]?.message || fallback;
  }

  // Edit-screen lines should still be written through setField so the post-actors run.
  static async grid(index: number = 0, timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<GridApi> {
    let api: GridApi | undefined;
    await this.waitFor(() => !!(api = this.visibleGridApi(index)), timeoutMs, `no visible grid #${index}`);
    return api!;
  }

  private static visibleGridApi(index: number): GridApi | undefined {
    const wrapper = Array.from(document.querySelectorAll<HTMLElement>(".ag-root-wrapper"))
      .filter(element => element.offsetParent !== null)[index];
    return wrapper && getGridApi(wrapper.closest<HTMLElement>("[grid-id]") ?? wrapper);
  }

  static errors() {
    return useErrorsStore().errors.map((error: any) => ({
      message: error.message?.englishText || error.message?.arabicText || "",
      row: error.row,
      field: error.relatedToOption,
    }));
  }

  static messages(): string[] {
    return useMessagesStore().messages.map((m: any) => m.message?.englishText || m.message?.arabicText || "");
  }

  static clearErrors(): void {
    useErrorsStore().clearErrors();
  }

  // Suppresses the notification/announcement panel and the error and message toasts, so a
  // documentation screenshot shows only the screen being documented. Also settable with ?capture=1.
  static captureMode(on: boolean = true): boolean {
    DocsCaptureMode.set(on);
    if (on) {
      useErrorsStore().clearErrors();
      useMessagesStore().clearMessages();
      useToast().clear();
    }
    return DocsCaptureMode.isActive();
  }

  // Lists every theme the user menu offers: the built-in system themes plus the server's GUITheme records.
  static async themes() {
    const themeStore = useThemeStore();
    await themeStore.loadThemes();
    return themeStore.themes.map(theme => ({
      code: theme.code,
      name1: theme.name1,
      name2: theme.name2,
      systemTheme: theme.systemTheme === true,
      darkNavBarAndSideBar: theme.darkNavBarAndSideBar === true,
    }));
  }

  // Applies a theme exactly as picking it from the user menu does. Pass dark to force the colour mode
  // in the same call; leave it out to keep the current one.
  static async setTheme(code: string, dark?: boolean) {
    await useThemeStore().fetchAndApplyTheme(code);
    if (dark !== undefined)
      useNamaFlagsStore().updateDarkMde(dark);
    await this.whenReady();
    return this.appearance();
  }

  static setDarkMode(dark: boolean) {
    useNamaFlagsStore().updateDarkMde(dark);
    return this.appearance();
  }

  // Opens or closes the main menu drawer. A click elsewhere on the page closes it again unless it is fixed.
  static async sideBar(open: boolean = true) {
    useNamaFlagsStore().isSideBarOpen = open;
    // Lets the drawer's slide-in transition finish before a screenshot is taken.
    await new Promise(resolve => setTimeout(resolve, 400));
    return this.appearance();
  }

  static appearance() {
    const flags = useNamaFlagsStore();
    return {
      themeCode: localStorage.getItem('theme-code'),
      darkMode: flags.darkMode,
      darkNavBarAndSideBar: flags.darkNavBarAndSideBar,
      sideBarOpen: flags.isSideBarOpen,
    };
  }

  // Not timerRunning: that is the spinner debounce, re-armed for 250 ms after every fast request.
  private static isLoading(): boolean {
    const loading = useLoadingStore();
    return loading.isFirstTimeLoadingActive || loading.loadingCount > 0;
  }

  // Sort, filter and paging reload rows without the loading counter; the server-side row model shows stub rows meanwhile.
  private static listRowsLoading(): boolean {
    return this.visibleGridApi(0)?.getRenderedNodes().some(node => node.stub) === true;
  }

  private static onListRoute(): boolean {
    return router.currentRoute.value.name === RouteNames.LIST_VIEW_ROUTE;
  }

  private static onEditRoute(): boolean {
    return router.currentRoute.value.name === RouteNames.EDIT_VIEW_ROUTE;
  }

  private static currentQuery(): Record<string, any> {
    return (router.currentRoute.value.query ?? {}) as Record<string, any>;
  }

  private static parsedPageId(raw: any): number | undefined {
    const pageId = Number.parseInt(String(raw));
    return Number.isNaN(pageId) ? undefined : pageId;
  }

  private static placeFor(options: GotoOptions) {
    const identified = ObjectChecker.isNotEmptyOrNull(options.id) || ObjectChecker.isNotEmptyOrNull(options.code);
    const place = identified
      ? PlaceUtils.editPlace(options.entityType, options.id)
      : PlaceUtils.newPlace(options.entityType);
    if (place && ObjectChecker.isEmptyOrNull(place.view))
      place.view = identified ? ViewMode.EDIT : ViewMode.NEW;
    return place;
  }

  // router.push never settles while a navigation guard is waiting on a modal, which is what happens
  // when you navigate away from a screen with unsaved changes. Bound it so that hangs as an error.
  private static async push(route: any, timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<void> {
    try {
      await this.withTimeout(router.push(route), timeoutMs,
        "navigation did not settle, an unsaved-changes confirmation is probably open; save or discard first");
    } catch (navigationRejected: any) {
      // vue-router rejects a push that resolves to the current location; that is a no-op, not a failure.
      if (!navigationRejected?.type)
        throw navigationRejected;
    }
  }

  private static withTimeout<T>(promise: Promise<T>, timeoutMs: number, description: string): Promise<T> {
    return Promise.race([promise, this.sleep(timeoutMs).then((): T => {
      throw new Error("timed out after " + timeoutMs + "ms: " + description);
    })]);
  }

  private static editPresenter(): any | undefined {
    const presenter: any = TopPanelSearchUtils.findAnyEditPresenter();
    return presenter?.isEditPresenter?.() ? presenter : undefined;
  }

  private static requireEditPresenter(): any {
    const presenter = this.editPresenter();
    if (!presenter)
      throw new Error("no edit screen is open");
    return presenter;
  }

  // The same checks the field widgets and grid cells use to decide whether the user may type into them.
  private static isLockedOnScreen(presenter: any, fieldId: string, row?: number): boolean {
    if (fieldId === "barcode")
      return false;
    const metadata = MetaData.findFieldMetadata(fieldId, presenter.fetchMetadata());
    if (metadata && (metadata.systemField || (metadata.guiField && !metadata.enabled)))
      return true;
    if (ObjectChecker.isEmptyOrNull(row) && row !== 0)
      return presenter.isFieldDisabled(fieldId) || presenter.isFieldHidden(fieldId);
    const collectionId = this.splitGridPath(fieldId).collectionId;
    const line = presenter.fetchRecord()[collectionId]?.[row as number];
    return presenter.isFieldDisabled(collectionId) || EditPresenter.getBaseDisabledFields().includes(fieldId)
      || presenter.isColumnDisabled(line?.id ?? "", collectionId, fieldId);
  }

  private static readPath(record: Record<string, any>, fieldId: string, row?: number): any {
    if (ObjectChecker.isEmptyOrNull(row) && row !== 0)
      return record[fieldId];
    const path = this.splitGridPath(fieldId);
    return record[path.collectionId]?.[row as number]?.[path.columnId];
  }

  private static writePath(record: Record<string, any>, fieldId: string, value: any, row?: number): void {
    if (ObjectChecker.isEmptyOrNull(row) && row !== 0) {
      record[fieldId] = value;
      return;
    }
    const path = this.splitGridPath(fieldId);
    const line = record[path.collectionId]?.[row as number];
    if (!line)
      throw new Error("no line " + row + " in collection " + path.collectionId);
    line[path.columnId] = value;
  }

  private static splitGridPath(fieldId: string) {
    const separator = fieldId.lastIndexOf(".");
    if (separator < 0)
      throw new Error("grid field id must be <collection>.<column>, got " + fieldId);
    return {collectionId: fieldId.slice(0, separator), columnId: fieldId.slice(separator + 1)};
  }

  private static serializableSearchItem(item: SearchItem) {
    return {
      id: item.id,
      type: item.type,
      text: item.text,
      code: item.code,
      entityType: item.record?.entityType,
      recordId: item.record?.id,
    };
  }

  // browser_evaluate serializes whatever it gets back, and records are reactive proxies that can
  // carry functions and cycles, so hand back a plain snapshot instead.
  private static toPlainValue(value: any): any {
    if (value === null || typeof value !== "object")
      return value;
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (notSerializable) {
      return String(value);
    }
  }

  private static async waitFor(condition: () => boolean, timeoutMs: number, description: string): Promise<void> {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      if (condition())
        return;
      await this.sleep(POLL_INTERVAL_MS);
    }
    throw new Error("timed out after " + timeoutMs + "ms: " + description);
  }

  // One sample is not enough: router.push resolves before the outgoing screen unmounts, and the next screen starts
  // loading from a watcher, so a lone sample can still read the old screen. Re-read once Vue has flushed.
  private static async waitForSettled(condition: () => boolean, timeoutMs: number, description: string): Promise<void> {
    const deadline = Date.now() + timeoutMs;
    do {
      await this.waitFor(condition, deadline - Date.now(), description);
      await nextTick();
      await this.yieldToEventLoop();
    } while (!condition());
  }

  // Unlike setTimeout, a MessageChannel task is not throttled while the browser window is hidden.
  private static yieldToEventLoop(): Promise<void> {
    return new Promise(resolve => {
      const channel = new MessageChannel();
      channel.port1.onmessage = () => resolve();
      channel.port2.postMessage(null);
    });
  }

  private static sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
